import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { ArrowRight, Users } from "lucide-react";

const HERO = "https://images.pexels.com/photos/29332767/pexels-photo-29332767.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TIMELINE = [
  { year: "1940", text: "Kazuo Chiba is born in Japan." },
  { year: "1958", text: "At eighteen, he is accepted as an uchideshi (live-in disciple) of the Founder of Aikido, Morihei Ueshiba O-Sensei, at Aikikai Hombu Dojo in Tokyo." },
  { year: "1966", text: "Dispatched abroad, Chiba Sensei helps establish Aikido in the United Kingdom." },
  { year: "1981", text: "He moves to California and founds San Diego Aikikai, which becomes the hub of his teaching in North America." },
  { year: "2000", text: "Founds Birankai International to unite his students worldwide." },
  { year: "2001", text: "Establishes Birankai North America (BNA), directly affiliated with Aikikai Hombu Dojo in Tokyo." },
  { year: "2015", text: "T.K. Chiba Shihan, 8th Dan, passes away on June 5th, leaving a profound legacy of rigorous, sincere practice." },
];

const SHOW_CONTRIBUTORS = false; // reserved — turn on once contributors have donated
const CONTRIBUTORS = [
  { name: "Liese Klein", dojo: "FireHorse Aikido" },
  { name: "Larry Levitt", dojo: "Valley Aikikai" },
  { name: "Yves Nazon", dojo: "Valley Aikido" },
  { name: "Rob Schenk", dojo: "Aikido Institute of San Francisco" },
];

export default function About() {
  return (
    <div className="min-h-screen grain">
      <Navbar />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0B0E]/70 via-[#0A0B0E]/85 to-[#0A0B0E]" />
        </div>
        <div className="relative px-6 md:px-12 lg:px-24 mx-auto max-w-4xl py-28 md:py-36">
          <p className="text-sm tracking-[0.35em] uppercase text-[#C5A059] mb-6 fade-up">About the Archive</p>
          <h1 className="font-serif text-5xl sm:text-6xl leading-[0.98] text-[#EBE9E1] fade-up" style={{ animationDelay: "80ms" }}>
            Birankai &amp; the Legacy of Chiba Sensei
          </h1>
        </div>
      </section>

      {/* Chiba Sensei */}
      <section className="px-6 md:px-12 lg:px-24 mx-auto max-w-4xl py-16 border-t border-white/10">
        <h2 className="font-serif text-3xl text-[#C5A059] mb-6">Kazuo (T.K.) Chiba Shihan</h2>
        <div className="space-y-5 text-[#EBE9E1]/90 leading-relaxed text-lg">
          <p>
            Kazuo Chiba Sensei (1940–2015) was among the last generation of live-in students to train
            directly under the Founder of Aikido, Morihei Ueshiba O-Sensei. Accepted as an uchideshi at
            Aikikai Hombu Dojo in 1958, he spent his formative years in the most rigorous and intimate
            environment of study, absorbing the art at its source.
          </p>
          <p>
            Entering Aikido in 1958 as an uchideshi at Aikikai Hombu Dojo, and dispatched to Britain in the
            1960s, Chiba Sensei was instrumental in establishing Aikido in the United
            Kingdom before settling in California, where he founded San Diego Aikikai in 1981. Holding the
            rank of 8th Dan, his teaching was known for its rigor, depth, and intensity in transmitting
            the practice of O-Sensei's Aikido. He later established Birankai. (1940–2015)
          </p>
        </div>
      </section>

      {/* Birankai founding */}
      <section className="px-6 md:px-12 lg:px-24 mx-auto max-w-4xl py-16 border-t border-white/10">
        <h2 className="font-serif text-3xl text-[#C5A059] mb-6">The Founding of Birankai</h2>
        <div className="space-y-5 text-[#EBE9E1]/90 leading-relaxed text-lg">
          <p>
            To unite the students who had gathered around his teaching, Chiba Sensei founded Birankai
            International in 2000, followed by Birankai North America (BNA) in 2001. Directly affiliated
            with and recognised by Aikikai Hombu Dojo in Tokyo, Birankai was created to uphold quality
            instruction and sincere practice — carrying his lineage forward with fidelity.
          </p>
          <p>
            Today, Birankai North America stewards that tradition through its member dojos, teaching
            certification, and the preservation of Chiba Sensei's teaching. This digital archive is part
            of that mission — a home for his recorded seminars, weapons work, techniques, interviews, and
            history, safeguarded for present and future students.
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section className="px-6 md:px-12 lg:px-24 mx-auto max-w-4xl py-16 border-t border-white/10">
        <h2 className="font-serif text-3xl text-[#C5A059] mb-10">A Brief Timeline</h2>
        <div className="space-y-0">
          {TIMELINE.map((t, i) => (
            <div key={t.year} className="flex gap-6 md:gap-10 py-5 border-b border-white/5 fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <span className="font-serif text-2xl text-[#C5A059] w-20 shrink-0">{t.year}</span>
              <p className="text-[#EBE9E1]/85 leading-relaxed">{t.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contributors — hidden until contributions are made (flip SHOW_CONTRIBUTORS to true) */}
      {SHOW_CONTRIBUTORS && (
      <section className="px-6 md:px-12 lg:px-24 mx-auto max-w-4xl py-16 border-t border-white/10">
        <Users size={28} strokeWidth={1.5} className="text-[#C5A059] mb-5" />
        <h2 className="font-serif text-3xl text-[#EBE9E1] mb-4">With Gratitude to Our Contributors</h2>
        <p className="text-[#8A8F98] leading-relaxed mb-8 max-w-2xl">
          This archive is made possible by the students, instructors, and custodians who have generously
          shared their recordings and collections with the Birankai community.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {CONTRIBUTORS.map((c) => (
            <div key={c.name} data-testid={`contributor-${c.name}`} className="border border-white/10 bg-[#121418] p-5">
              <p className="font-serif text-xl text-[#EBE9E1]">{c.name}</p>
              {c.dojo && <p className="text-sm text-[#C5A059] mt-1">{c.dojo}</p>}
            </div>
          ))}
        </div>
        <p className="text-[#8A8F98] text-sm mt-6">…and many others across the Birankai community.</p>
      </section>
      )}

      <section className="px-6 md:px-12 lg:px-24 mx-auto max-w-4xl py-16 border-t border-white/10 text-center">
        <h2 className="font-serif text-3xl text-[#EBE9E1] mb-6">Support the Preservation</h2>
        <p className="text-[#8A8F98] mb-8 max-w-xl mx-auto">This is a beta for the Board and Senior Council of Birankai North America, supporting its mission to preserve and teach Chiba Sensei's Aikido.</p>
        <Link to="/register" data-testid="about-join-btn" className="btn-primary inline-flex items-center gap-2 px-8 py-4 font-medium">
          Create Free Account <ArrowRight size={18} />
        </Link>
      </section>

      <footer className="px-6 md:px-12 lg:px-24 mx-auto max-w-7xl py-16 border-t border-white/10 text-[#8A8F98] text-sm space-y-3">
        <p className="font-serif text-xl text-[#EBE9E1]">Official T.K. Chiba Shihan Archive</p>
        <p>Presented by Birankai North America. In memory of Kazuo (T.K.) Chiba Shihan, 1940–2015.</p>
        <p className="text-[#555] max-w-3xl pt-3">
          Content is included by permission of the copyright holders or under archive contributor agreement.
          Additional material is added as rights are confirmed. All rights remain with their respective owners.
        </p>
      </footer>
    </div>
  );
}
